import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchMerchantApprovals,
  MerchantApproval,
} from "../../services/merchantApproval";

export interface MerchantApprovalState {
  merchantApprovals: MerchantApproval[];
  total: number;
  loading: boolean;

  page: number;
  pageSize: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  searchQuery: string;

  hasFetched: boolean;
  error?: string;
}

const initialState: MerchantApprovalState = {
  merchantApprovals: [],
  total: 0,
  loading: false,

  page: 1,
  pageSize: 10,
  sortBy: "createdAt",
  sortOrder: "desc",
  searchQuery: "",

  hasFetched: false,
};

export const fetchMerchantApprovalsThunk = createAsyncThunk(
  "merchantApproval/fetchMerchantApprovals",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as {
        merchantApproval: MerchantApprovalState;
        auth: { token: string };
      };

      const { token } = state.auth;
      const { page, pageSize, sortBy, sortOrder, searchQuery } =
        state.merchantApproval;

      const res = await fetchMerchantApprovals({
        page,
        pageSize,
        search: searchQuery,
        sortBy,
        sortOrder,
        token,
      });

      return {
        merchantApprovals: res.data,
        total: res.pagination.totalMatch,
        page: res.pagination.page,
        pageSize: res.pagination.pageSize,
      };
    } catch (err: any) {
      return rejectWithValue(err?.message || "Failed to fetch merchant approvals");
    }
  }
);

const merchantApprovalSlice = createSlice({
  name: "merchantApproval",
  initialState,

  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<"asc" | "desc">) => {
      state.sortOrder = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setHasFetched: (state, action: PayloadAction<boolean>) => {
      state.hasFetched = action.payload;
    },
    resetFilters: (state) => {
      state.page = 1;
      state.pageSize = 10;
      state.sortBy = "createdAt";
      state.sortOrder = "desc";
      state.searchQuery = "";
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchMerchantApprovalsThunk.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchMerchantApprovalsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.merchantApprovals = action.payload.merchantApprovals;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.hasFetched = true;
      })
      .addCase(fetchMerchantApprovalsThunk.rejected, (state, action) => {
        state.loading = false;
        state.merchantApprovals = [];
        state.total = 0;
        state.error = action.payload as string;
      });
  },
});

export const {
  setPage,
  setPageSize,
  setSortBy,
  setSortOrder,
  setSearchQuery,
  setHasFetched,
  resetFilters,
} = merchantApprovalSlice.actions;

export default merchantApprovalSlice.reducer;